import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientSlider from "../components/home/ClientSlider";
import TeamSection from "../components/home/TeamSection";
import TestimonialSection from "../components/home/TestimonialSection";
import Seo from "../components/Seo";
import { loadSection } from "../services/contentSource";
import testimonialsFallback from "../data/home/testimonials.json";
import aboutPageFallback from "../data/aboutPage.json";

const About = () => {
  const [testimonialData, setTestimonialData] = useState<any>(testimonialsFallback);
  const [page, setPage] = useState<any>(aboutPageFallback);

  useEffect(() => {
    loadSection("testimonials", testimonialsFallback as any).then(({ data: main }) => {
      if (main) setTestimonialData(main);
    });
    loadSection("aboutPage", aboutPageFallback as any).then(({ data: main }) => {
      if (main) setPage(main);
    });
  }, []);

  const heroTitle =
    page?.hero?.title ||
    "WE BUILD DIGITAL PRODUCTS THAT THINK, ADAPT, AND DELIVER";
  const heroLede =
    page?.hero?.lede ||
    "From concept to autonomous production AI, MindSyncX is an engineering powerhouse crafting intelligent ecosystems, scalable cloud infrastructure, and transformative digital experiences.";

  const testimonials = (testimonialData?.testimonials || []).slice(0, 3);

  return (
    <>
      <Seo
        title="Why MindSyncX | Enterprise AI Engineering"
        description="An engineering company that ships and runs production AI systems, not prototypes. Who we are and how we work."
        path="/about"
      />

      <main className="mx-about-revamp">
        {/* 1. Hero Area */}
        <section className="mx-about-hero-section">
          <div className="container mx-container">
            <div className="mx-about-hero-header">
              <div className="mx-hero-badge">
                <span className="mx-hero-badge-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                </span>
                <span>01. WHO WE ARE & WHY MINDSYNCX</span>
              </div>

              <h1 className="mx-about-hero-title">
                {heroTitle}
              </h1>

              <p className="mx-about-hero-lede">
                {heroLede}
              </p>
            </div>
          </div>
        </section>

        {/* 2. Telemetry & Impact Stats Grid */}
        <section className="mx-telemetry-section">
          <div className="container mx-container">
            <div className="mx-telemetry-grid">
              <div className="mx-telemetry-card">
                <div className="mx-telemetry-top">
                  <span className="mx-telemetry-tag">METRIC // 01</span>
                  <svg className="mx-telemetry-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="mx-telemetry-num">50+</div>
                <div className="mx-telemetry-label">Deployments &amp; Systems Shipped</div>
                <p className="mx-telemetry-desc">Production AI applications and digital platforms engineered for ambitious brands.</p>
              </div>

              <div className="mx-telemetry-card">
                <div className="mx-telemetry-top">
                  <span className="mx-telemetry-tag">METRIC // 02</span>
                  <svg className="mx-telemetry-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                  </svg>
                </div>
                <div className="mx-telemetry-num">10x</div>
                <div className="mx-telemetry-label">Production Velocity</div>
                <p className="mx-telemetry-desc">Accelerated sprint-to-production deployment powered by custom AI tooling.</p>
              </div>

              <div className="mx-telemetry-card">
                <div className="mx-telemetry-top">
                  <span className="mx-telemetry-tag">METRIC // 03</span>
                  <svg className="mx-telemetry-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="mx-telemetry-num">$25M+</div>
                <div className="mx-telemetry-label">Funding &amp; Scale Enabled</div>
                <p className="mx-telemetry-desc">Capital raised and value unlocked by platforms architected by MindSyncX.</p>
              </div>

              <div className="mx-telemetry-card">
                <div className="mx-telemetry-top">
                  <span className="mx-telemetry-tag">METRIC // 04</span>
                  <svg className="mx-telemetry-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div className="mx-telemetry-num">99.4%</div>
                <div className="mx-telemetry-label">Systems Reliability Uptime</div>
                <p className="mx-telemetry-desc">Continuous telemetry monitoring with automated healing &amp; failovers.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Client Ecosystem Marquee */}
        <ClientSlider label="COLLABORATING WITH FORWARD-THINKING BRANDS &amp; HIGH-GROWTH TEAMS" />

        {/* 3. Strategic Capabilities & Pillars */}
        <section className="mx-pillars-section">
          <div className="container mx-container">
            <div className="mx-pillars-header">
              <div className="mx-hero-badge">
                <span className="mx-hero-badge-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </span>
                <span>02. STRATEGIC ARCHITECTURE</span>
              </div>

              <h2 className="mx-pillars-title">
                WE CRAFT SCALABLE STRATEGY TO ACCELERATE YOUR ENTERPRISE
              </h2>

              <p className="mx-pillars-lede">
                We deploy world-class engineering squads on demand to build, ship, and scale your vision—efficiently, securely, and with verified impact.
              </p>
            </div>

            <div className="mx-pillars-grid">
              <div className="mx-pillar-card">
                <div className="mx-pillar-icon-box">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                </div>
                <h3 className="mx-pillar-card-title">Autonomous AI Systems</h3>
                <p className="mx-pillar-card-desc">
                  Multi-agent workflows and contextual intelligence tuned for high-throughput enterprise execution with zero hallucinations.
                </p>
              </div>

              <div className="mx-pillar-card">
                <div className="mx-pillar-icon-box">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                </div>
                <h3 className="mx-pillar-card-title">Cloud-Native Scale</h3>
                <p className="mx-pillar-card-desc">
                  Kubernetes, distributed microservices, and serverless edge functions architected for elastic load and sub-second latency.
                </p>
              </div>

              <div className="mx-pillar-card">
                <div className="mx-pillar-icon-box">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
                <h3 className="mx-pillar-card-title">Zero Trust Security</h3>
                <p className="mx-pillar-card-desc">
                  Enterprise-grade data encryption at rest and in transit, strict RBAC, and automated SOC2 / HIPAA compliance telemetry.
                </p>
              </div>

              <div className="mx-pillar-card">
                <div className="mx-pillar-icon-box">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                </div>
                <h3 className="mx-pillar-card-title">Dedicated Technical Leadership</h3>
                <p className="mx-pillar-card-desc">
                  We don't hand off prototypes and disappear. Every engagement is backed by senior architects and engineers who support, optimize, and scale your solutions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Enterprise Innovation Showcase */}
        <section className="mx-showcase-section">
          <div className="container mx-container">
            <div className="mx-showcase-card">
              <div className="mx-showcase-content">
                <div className="mx-showcase-badge">
                  <span className="mx-showcase-badge-dot"></span>
                  <span>{page.runWhatWeBuild?.badge || "OUR ENGINEERING CULTURE"}</span>
                </div>

                <h2 className="mx-showcase-title">
                  {page.runWhatWeBuild?.title || "AGILE STARTUP VELOCITY BACKED BY ENTERPRISE RIGOR"}
                </h2>

                <p className="mx-showcase-text">
                  {page.runWhatWeBuild?.textBefore || "MindSyncX is a lean, high-conviction technology studio built for the AI era. We eliminate corporate bureaucracy, middlemen, and slow agency handoffs to deliver production-ready, intelligent systems with unmatched execution speed."}
                </p>

                <div className="mx-showcase-features">
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Zero Bureaucracy</span>
                  </div>
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Full-Stack AI Specialization</span>
                  </div>
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Senior Squads Only</span>
                  </div>
                </div>

                <div className="mx-showcase-btn-group">
                  <Link to={page.runWhatWeBuild?.buttonLink || "/contact"} className="mx-showcase-btn">
                    <span>{page.runWhatWeBuild?.buttonText || "Partner With Us"}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>

                  <Link to="#team" className="mx-showcase-btn-secondary">
                    <span>Meet The Leadership</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="mx-showcase-visual-panel">
                <div className="mx-showcase-terminal-header">
                  <div className="mx-showcase-terminal-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="mx-showcase-terminal-status">THE MINDSYNCX PLAYBOOK // CORE PRINCIPLES</div>
                </div>

                <div className="mx-dashboard-status-banner">
                  <div className="mx-dashboard-status-indicator">
                    <span className="mx-dashboard-pulse-dot"></span>
                    <span className="mx-dashboard-status-title">High-Conviction Engineering</span>
                  </div>
                  <span className="mx-dashboard-status-sub">Built for visionary founders &amp; ambitious enterprises</span>
                </div>

                <div className="mx-dashboard-metrics-grid">
                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">SPRINT VELOCITY</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">10x</div>
                    <div className="mx-dashboard-metric-note">From concept to production-ready</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">CLIENT SATISFACTION</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">100%</div>
                    <div className="mx-dashboard-metric-note">Direct senior engineer engagement</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">CORE DNA</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">AI-Native</div>
                    <div className="mx-dashboard-metric-note">Intelligent automations &amp; models</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">PARTNER RETENTION</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">98%</div>
                    <div className="mx-dashboard-metric-note">Long-term scaling &amp; evolution</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Verified Client Impact (Testimonials) */}
        <TestimonialSection badgeText="04. CLIENT VOICES &amp; TESTIMONIALS" />

        {/* 6. Leadership & Architects */}
        <TeamSection badgeText="05. LEADERSHIP &amp; ARCHITECTS" />

        {/* 7. Closing Call To Action */}
        <section className="mx-page-cta-section">
          <div className="container mx-container">
            <div className="mx-page-cta-box">
              <h2 className="mx-page-cta-title">
                {page.closingCta?.title || "START YOUR EXPERIENCE WITH MINDSYNCX"}
              </h2>
              <Link to="/contact" className="mx-page-cta-btn">
                <span>{page.closingCta?.buttonText || "Let’s get in touch"}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;
